import { Card, Flex, Tabs, theme } from 'antd';
import { useQuery } from 'jsonapi-react';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CycleMenuSitesGroupsComponent } from './cycle-menu-sites-groups.component';
import { buildParentEntityToSite } from './cycles-menu.page';
import AssignedBeveragesPage from './scheduled-meals/assigned-beverages.page';
import AssignedSuppliesPage from './scheduled-meals/assigned-supplies.page';
import ScheduledMealsPage from './scheduled-meals/scheduled-meals.page';
import LoadingSpin from '../../../components/loading-spin';
import SyncSuspense from '../../../components/sync-suspense';
import { type CycleMenuResponseDto } from '../../../dto/cycle-menu/cycle-menu-response-dto';
import { type ResponseDto } from '../../../dto/ResponseDto';
import { type Customer, type Meal, type MealCategory, type MealItem } from '../../../entities';
import { useNotification } from '../../../hooks/use-notification';
import useSecuredApiV2 from '../../../hooks/use-secured-api-v2';
import { BreadcrumbParamsContext } from '../../../layout';
import { type DataContext } from '../../../types';
import { CYCLE_MENUS_ENDPOINT_URL } from '../../../utils/endpoints';

export const MealsContext = createContext<DataContext<Meal[]>>({});
export const MealItemsContext = createContext<DataContext<MealItem[]>>({});
export const MealCategoriesContext = createContext<DataContext<MealCategory[]>>({});

const CycleMenuSchedulesPage = (): ReactNode => {
  const { token } = theme.useToken();
  const { setBreadcrumbParams } = useContext(BreadcrumbParamsContext);
  const notification = useNotification();
  const api = useSecuredApiV2();
  const { cycleMenuId } = useParams();

  const [cycleMenu, setCycleMenu] = useState<CycleMenuResponseDto | null>(null);
  const [isLoadingCycleMenu, setIsLoadingCycleMenu] = useState<boolean>(true);

  // Fetch cycle menu details via Spring Boot REST v2
  useEffect(() => {
    if (!cycleMenuId) return;
    setIsLoadingCycleMenu(true);
    const sub = api.get(`${CYCLE_MENUS_ENDPOINT_URL}/${cycleMenuId}`, {}, {}).subscribe({
      next: (res: ResponseDto<CycleMenuResponseDto>) => {
        setCycleMenu(res.data);
        setIsLoadingCycleMenu(false);
      },
      error: (err: any) => {
        notification.error({
          message: err?.title ?? '',
          description:
            err?.detail ??
            err?.message ??
            (typeof err === 'string' ? err : 'Failed to load cycle menu details'),
        });
        setIsLoadingCycleMenu(false);
      },
    });

    return () => {
      sub.unsubscribe();
    };
  }, [cycleMenuId]);

  // Master customer-site hierarchy for top site groups component
  const customers = useQuery<Customer[]>([
    'customer',
    {
      filter: 'isInactive==false',
      sort: 'name',
      fields: {
        customer: ['name', 'sites'],
        site: [
          'name',
          'state',
          'county',
          'agency',
          'siteNumber',
          'custSiteNumber',
          'displaySiteNumber',
        ],
        agency: ['name'],
      },
      include: ['sites.agency'],
      page: { size: 10000 },
    },
  ]);

  // Master data queries
  const mealItems = useQuery<MealItem[]>([
    'meal-item',
    { fields: { 'meal-item': ['name'] }, page: { limit: 10000 } },
  ]);

  const mealCategories = useQuery<MealCategory[]>([
    'meal-category',
    {
      fields: { 'meal-category': ['name', 'isGeneric', 'availableWeekDays'] },
      page: { limit: 10000 },
      sort: 'sortOrder',
    },
  ]);

  const meals = useQuery<Meal[]>([
    'meal',
    {
      fields: { meal: ['name', 'definedMealCategories', 'season', 'weekNumber'] },
      page: { limit: 10000 },
      include: ['definedMealCategories'],
    },
  ]);

  const isLoading = useMemo(() => {
    return (
      isLoadingCycleMenu ||
      meals.isLoading ||
      mealItems.isLoading ||
      mealCategories.isLoading ||
      customers.isLoading
    );
  }, [
    isLoadingCycleMenu,
    meals.isLoading,
    mealItems.isLoading,
    mealCategories.isLoading,
    customers.isLoading,
  ]);

  // Update breadcrumb
  useEffect(() => {
    setBreadcrumbParams({ cycleMenuName: '...' });
  }, []);

  useEffect(() => {
    if (cycleMenu?.name) {
      setBreadcrumbParams({ cycleMenuName: cycleMenu.name });
    }
  }, [cycleMenu?.name]);

  return (
    <Flex gap="middle" vertical>
      <SyncSuspense
        fallback={
          <Card>
            <LoadingSpin />
          </Card>
        }
        condition={!isLoading}
      >
        <MealsContext.Provider value={meals}>
          <MealCategoriesContext.Provider value={mealCategories}>
            <MealItemsContext.Provider value={mealItems}>
              {cycleMenu && (
                <>
                  {/* Top section: Assigned Sites */}
                  <Flex
                    style={{
                      backgroundColor: token.colorFillContent,
                      borderRadius: token.borderRadiusLG,
                      padding: token.paddingSM,
                      border: `1px solid ${token.colorBorder}`,
                    }}
                  >
                    <CycleMenuSitesGroupsComponent
                      cycleMenu={cycleMenu}
                      parentEntityToSites={buildParentEntityToSite(
                        customers.data?.flatMap(
                          (customer) => customer?.sites.map((site) => ({ ...site, customer }))
                        ) ?? []
                      )}
                    />
                  </Flex>

                  {/* Center section: Independent Tabs */}
                  <Tabs
                    defaultActiveKey="1"
                    animated={{ inkBar: true, tabPane: false }}
                    items={[
                      {
                        key: '1',
                        label: 'Scheduled Meals',
                        children: <ScheduledMealsPage cycleMenu={cycleMenu} />,
                      },
                      {
                        key: '2',
                        label: 'Beverages',
                        children: <AssignedBeveragesPage cycleMenu={cycleMenu} />,
                      },
                      {
                        key: '3',
                        label: 'Supplies',
                        children: <AssignedSuppliesPage cycleMenu={cycleMenu} />,
                      },
                    ]}
                  />
                </>
              )}
            </MealItemsContext.Provider>
          </MealCategoriesContext.Provider>
        </MealsContext.Provider>
      </SyncSuspense>
    </Flex>
  );
};

export default CycleMenuSchedulesPage;
